/**
 * YouTubeCandidatesDialog - Muestra hasta 3 resultados de YouTube para que el DJ
 * elija la versión correcta antes de descargar el MP3.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RefreshCw, Download, Music2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';

const formatDuration = (sec) => {
  if (!sec || isNaN(sec)) return '';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const YouTubeCandidatesDialog = ({ open, onOpenChange, peticion, playlistId, onSuccess, force = false }) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!open || !peticion) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setCandidates([]);
      setSelected(null);
      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${BACKEND_URL}/api/peticiones/youtube-search`,
          {
            title: peticion.titulo || peticion.title || '',
            artist: peticion.artista || peticion.artist || '',
            spotify_id: peticion.spotify_id || null,
            limit: 3,
          },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
        );
        if (cancelled) return;
        const list = res.data.candidates || [];
        setCandidates(list);
        if (list.length > 0) setSelected(list[0].youtube_url);
      } catch (e) {
        if (cancelled) return;
        toast.error(e.response?.data?.detail || 'Error buscando en YouTube');
        onOpenChange(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // Note: peticion?.id intentionally only — re-fetch only when dialog opens or petición changes
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: onOpenChange and peticion
  }, [open, peticion?.id]);

  const handleDownload = async () => {
    if (!selected) return;
    setDownloading(true);
    const toastId = toast.loading('Descargando MP3 del video elegido…');
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/peticiones/${peticion.id}/resolve-and-download`,
        null,
        {
          params: { playlist_id: playlistId, youtube_url: selected, ...(force ? { force: true } : {}) },
          headers: { Authorization: `Bearer ${token}` },
          timeout: 150000,
        }
      );
      if (res.data.success) {
        toast.success(`MP3 descargado (${res.data.file_size_mb} MB)`, { id: toastId });
        // Fix iter91.1: cerrar el diálogo ANTES de refrescar la playlist,
        // para que el re-render del parent (con nueva referencia de `peticion`)
        // no reabra el flujo de búsqueda mientras el modal aún está abierto.
        onOpenChange(false);
        if (typeof onSuccess === 'function') onSuccess(res.data);
      } else {
        toast.warning(res.data.user_message || 'No se pudo descargar', { id: toastId, duration: 8000 });
        if (typeof onSuccess === 'function') onSuccess(res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error descargando', { id: toastId });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 max-w-lg" data-testid="yt-candidates-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">
            {force ? 'Cambiar versión de YouTube' : 'Elige la versión de YouTube'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {peticion?.artista || peticion?.artist} — {peticion?.titulo || peticion?.title}
            {force && (
              <div className="mt-1 text-yellow-400/80 text-xs">
                ⚠ El MP3 actual será reemplazado por el que elijas.
              </div>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-96 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-400 p-4">
              <RefreshCw className="w-4 h-4 animate-spin" /> Buscando candidatos…
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-slate-400 text-sm p-4">Sin resultados</p>
          ) : (
            candidates.map((c) => (
              <label
                key={c.video_id || c.youtube_url}
                className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer border transition-colors ${
                  selected === c.youtube_url
                    ? 'bg-cyan-900/30 border-cyan-700/50'
                    : 'bg-slate-800/40 border-slate-700/40 hover:bg-slate-800/70'
                }`}
                data-testid={`yt-candidate-${c.video_id}`}
              >
                <input
                  type="radio"
                  name="yt-candidate"
                  checked={selected === c.youtube_url}
                  onChange={() => setSelected(c.youtube_url)}
                  className="flex-shrink-0"
                />
                {c.thumbnail ? (
                  <img
                    src={c.thumbnail}
                    alt={c.title}
                    className="w-16 h-12 rounded object-cover flex-shrink-0"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-16 h-12 rounded bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <Music2 className="w-4 h-4 text-slate-500" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm truncate font-medium">{c.title}</div>
                  <div className="text-slate-400 text-xs truncate">
                    {c.channel} {c.duration ? `· ${formatDuration(c.duration)}` : ''}
                  </div>
                </div>
              </label>
            ))
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={downloading}
            className="border-slate-600"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDownload}
            disabled={!selected || downloading || loading}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="yt-candidate-download-btn"
          >
            {downloading ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Descargando…
              </>
            ) : (
              <>
                <Download className="w-4 h-4 mr-2" />
                Descargar MP3
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default YouTubeCandidatesDialog;
